import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/lib/supabase';
import { ApiCallError, callFunction } from '@/lib/api';
import { inviteAcceptErrorI18nKey, recoveryRedirectLocation } from '@/lib/authFormErrors';
import { AuthShell } from './AuthShell';
import { PasswordInput } from '@/components/ui/PasswordInput';
import { Button } from '@/components/ui/Button';
import { ErrorAlert } from '@/components/ui/ErrorAlert';

interface AcceptInvitationOutput {
  user_id: string;
  email: string;
  clinic_id: string;
  role: string;
  session: {
    access_token: string;
    refresh_token: string;
  };
}

export function AcceptInvitation() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const recoveryTo = recoveryRedirectLocation(location.pathname, location.search, location.hash);
  const token = params.get('token');

  if (recoveryTo) {
    return <Navigate to={recoveryTo} replace />;
  }

  if (!token) {
    return (
      <AuthShell
        title={t('auth.accept_invitation.title')}
        subtitle={t('auth.accept_invitation.subtitle')}
        footer={
          <Link to="/sign-in" className="font-medium text-ds-teal hover:text-ds-teal-700">
            {t('auth.forgot_password.back_to_sign_in')}
          </Link>
        }
      >
        <ErrorAlert message={t('auth.accept_invitation.missing_token')} />
      </AuthShell>
    );
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);

    if (password.length < 8) {
      setError(t('auth.reset_password.min_length'));
      return;
    }
    if (password !== confirm) {
      setError(t('auth.reset_password.mismatch'));
      return;
    }

    setSubmitting(true);
    try {
      const result = await callFunction<
        { token: string; password: string },
        AcceptInvitationOutput
      >('accept-invitation', { token, password });

      const { error: setErr } = await supabase.auth.setSession({
        access_token: result.session.access_token,
        refresh_token: result.session.refresh_token,
      });
      if (setErr) throw setErr;

      navigate('/dashboard', { replace: true });
    } catch (err) {
      if (err instanceof ApiCallError) {
        setError(t(inviteAcceptErrorI18nKey(err.code, 'staff')));
      } else {
        setError(t('auth.errors.unknown'));
      }
      setSubmitting(false);
    }
  };

  return (
    <AuthShell
      title={t('auth.accept_invitation.title')}
      subtitle={t('auth.accept_invitation.subtitle')}
      footer={
        <Link to="/sign-in" className="font-medium text-ds-teal hover:text-ds-teal-700">
          {t('auth.forgot_password.back_to_sign_in')}
        </Link>
      }
    >
      {error && <ErrorAlert message={error} />}
      <form onSubmit={handleSubmit} className="space-y-6">
        <PasswordInput
          label={t('auth.accept_invitation.password')}
          name="password"
          autoComplete="new-password"
          required
          minLength={8}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          hint={t('auth.accept_invitation.password_hint')}
        />
        <PasswordInput
          label={t('auth.accept_invitation.password_confirm')}
          name="passwordConfirm"
          autoComplete="new-password"
          required
          minLength={8}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
        />
        <Button type="submit" size="full" disabled={submitting}>
          {submitting ? t('auth.accept_invitation.submitting') : t('auth.accept_invitation.submit')}
        </Button>
      </form>
    </AuthShell>
  );
}
